import { useState, useRef, useCallback, MutableRefObject } from 'react';
import AssistantRagStorageService from '@shared/services/AssistantRagStorageService';
import { IAssistantRagVectorStorage } from '@shared/interfaces/assistantRagStorage';
import {
  assistantMapKey,
  matchesAssistantMap,
} from '@shared/utils/assistantMap';
import { debugMapName } from '@core/assistant/constants';
import { log } from './Debug';

export type UseAssistantKnowledgeParams = {
  mytaverseEventRef: MutableRefObject<any>;
  activeBrandingIdRef: MutableRefObject<string | undefined>;
};

/**
 * Loads the AI assistant's RAG knowledge items, scoped to the current
 * map/branding the same way useAssistantScripts scopes scripts.
 *
 * useRegionHandler's "no script for region" branch reads `ragItemsRef`
 * alongside `scriptsRef` to decide whether free-form Q&A has anything to
 * offer (a global item with no `mapName`, or an item tagged for the
 * region/trigger just entered) before leaving the assistant panel open.
 *
 * @returns `ragItems`/`ragItemsRef` (scoped RAG items) and `fetchKnowledge`,
 *   which reloads them and resolves with the new list.
 */
export const useAssistantKnowledge = ({
  mytaverseEventRef,
  activeBrandingIdRef,
}: UseAssistantKnowledgeParams) => {
  const [ragItems, setRagItems] = useState<IAssistantRagVectorStorage[]>([]);

  const ragItemsRef = useRef(ragItems);
  ragItemsRef.current = ragItems;

  const fetchKnowledge = useCallback(async () => {
    const fetchedItems =
      await AssistantRagStorageService.getAssistantRagStorageItems();

    log('loaded assistant knowledge', fetchedItems);

    // Scope by the canonical map key, matching useAssistantScripts.fetchScripts.
    const currentMap =
      mytaverseEventRef.current?.currentRoom?.map ?? debugMapName;
    const currentMapName = assistantMapKey(currentMap);
    const currentBrandingId =
      mytaverseEventRef.current?.currentRoom?.activeBrandingId ||
      activeBrandingIdRef.current;

    const scopedItems = fetchedItems.filter((item) => {
      if (currentBrandingId && item.brandingId !== currentBrandingId) {
        return false;
      }
      // A knowledge item with no mapName is global — it belongs to every
      // map (matches EditorMode/AiScripts/aggregate.ts's filterByScope).
      if (
        currentMapName &&
        item.mapName &&
        !matchesAssistantMap(item.mapName, currentMap)
      ) {
        return false;
      }
      return true;
    });

    log('filtered assistant knowledge', {
      currentMapName,
      currentBrandingId,
      count: scopedItems.length,
    });

    setRagItems(scopedItems);
    return scopedItems;
  }, []);

  return { ragItems, setRagItems, ragItemsRef, fetchKnowledge };
};
